import { useState, useEffect, useCallback, useMemo } from "react";

const PINNED_JOBS_STORAGE_KEY = "3dcolor-pinned-jobs";

// Custom event for immediate updates
const PINNED_JOBS_UPDATED_EVENT = "pinned-jobs-updated";

/**
 * PinnedJobs component
 * Shows quotes that have jobs pinned after PROOF-PDF completion
 * Groups jobs by quote, shows summary, hover for job list, click to navigate
 */
export default function PinnedJobs({
  workCenter,
  onSelectQuote, // New: navigate to quote/order
  activeSessions = {},
}) {
  const [pinnedJobs, setPinnedJobs] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredQuote, setHoveredQuote] = useState(null);

  // Load pinned jobs from localStorage
  const loadPinnedJobs = useCallback(() => {
    try {
      const stored = localStorage.getItem(PINNED_JOBS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const jobs = Array.isArray(parsed) ? parsed : [];
        setPinnedJobs(jobs);
      } else {
        setPinnedJobs([]);
      }
    } catch (err) {
      console.error("Error loading pinned jobs:", err);
    }
  }, []);

  // Load on mount and listen for updates
  useEffect(() => {
    loadPinnedJobs();

    // Listen for updates from usePinnedJobs hook
    const handleUpdate = () => {
      loadPinnedJobs();
    };
    window.addEventListener(PINNED_JOBS_UPDATED_EVENT, handleUpdate);
    return () => window.removeEventListener(PINNED_JOBS_UPDATED_EVENT, handleUpdate);
  }, [loadPinnedJobs]);

  // Save pinned jobs to localStorage
  const savePinnedJobs = useCallback((jobs) => {
    try {
      localStorage.setItem(PINNED_JOBS_STORAGE_KEY, JSON.stringify(jobs));
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent(PINNED_JOBS_UPDATED_EVENT));
    } catch (err) {
      console.error("Error saving pinned jobs:", err);
    }
  }, []);

  // Remove all jobs for a quote
  const removeQuoteJobs = useCallback((quoteNumber) => {
    setPinnedJobs((prev) => {
      const updated = prev.filter((j) => j.quoteNumber !== quoteNumber);
      savePinnedJobs(updated);
      return updated;
    });
  }, [savePinnedJobs]);

  // Clear all pinned jobs
  const clearAllPinned = useCallback(() => {
    setPinnedJobs([]);
    savePinnedJobs([]);
  }, [savePinnedJobs]);

  // Check if any job in quote is currently being worked on
  const getQuoteActiveState = useCallback((jobs) => {
    for (const job of jobs) {
      const state = activeSessions[job.id]?.state;
      if (state === "running") return "running";
      if (state === "paused") return "paused";
    }
    return null;
  }, [activeSessions]);

  // Group jobs by quote
  const groupedByQuote = useMemo(() => {
    if (workCenter !== "PA") return [];

    const groups = {};
    for (const job of pinnedJobs) {
      const key = job.quoteNumber || "no-quote";
      if (!groups[key]) {
        groups[key] = {
          quoteNumber: job.quoteNumber,
          orderNumber: job.orderNumber,
          projectName: job.projectName,
          customer: job.customer,
          projectManager: job.projectManager,
          jobs: [],
        };
      }
      groups[key].jobs.push(job);
    }

    // Sort by most recent pinned job in each group
    return Object.values(groups).sort((a, b) => {
      const aLatest = Math.max(...a.jobs.map(j => new Date(j.pinnedAt).getTime()));
      const bLatest = Math.max(...b.jobs.map(j => new Date(j.pinnedAt).getTime()));
      return bLatest - aLatest;
    });
  }, [pinnedJobs, workCenter]);

  // Don't render if not in PA work center or no pinned jobs
  if (workCenter !== "PA" || groupedByQuote.length === 0) {
    return null;
  }

  // Total job count
  const totalJobs = pinnedJobs.length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-lg hover:shadow-purple-500/10 transition-shadow">
      {/* Compact Header */}
      <div
        className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-slate-200"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-amber-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
          </svg>
          <span className="text-sm font-medium text-gray-700">My Jobs</span>
          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
            {groupedByQuote.length} quote{groupedByQuote.length !== 1 ? "s" : ""} • {totalJobs} job{totalJobs !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAllPinned();
              }}
              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-0.5 rounded hover:bg-slate-100"
            >
              Clear All
            </button>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-gray-500 transition-transform ${
              isCollapsed ? "" : "rotate-180"
            }`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Quote Cards */}
      {!isCollapsed && (
        <div className="px-3 pb-3">
          <div className="flex flex-wrap gap-2">
            {groupedByQuote.map((group) => {
              const activeState = getQuoteActiveState(group.jobs);
              const isActive = activeState === "running" || activeState === "paused";
              const isHovered = hoveredQuote === group.quoteNumber;

              return (
                <div
                  key={group.quoteNumber || "no-quote"}
                  className="relative"
                  onMouseEnter={() => setHoveredQuote(group.quoteNumber)}
                  onMouseLeave={() => setHoveredQuote(null)}
                >
                  {/* Quote Card */}
                  <div
                    className={`
                      group flex items-center gap-3 px-3 py-2 rounded-md border transition-all cursor-pointer
                      ${
                        isActive
                          ? "bg-green-50 border-green-300"
                          : "bg-slate-200 border-gray-200 hover:border-amber-400 hover:bg-amber-50"
                      }
                    `}
                    onClick={() => onSelectQuote?.({
                      quoteNumber: group.quoteNumber,
                      orderNumber: group.orderNumber,
                    })}
                  >
                    {/* Quote Info */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-amber-600 font-medium">
                          Q{group.quoteNumber || "—"}
                        </span>
                        <span className="text-xs text-gray-500">
                          #{group.orderNumber || "—"}
                        </span>
                      </div>
                      {/* Project Name */}
                      {group.projectName && (
                        <span className="text-xs text-gray-700 truncate max-w-[160px]">
                          {group.projectName}
                        </span>
                      )}
                    </div>

                    {/* Client & PM */}
                    <div className="flex flex-col text-xs border-l border-gray-200 pl-3">
                      {group.customer && (
                        <span className="text-gray-600 truncate max-w-[100px]">{group.customer}</span>
                      )}
                      {group.projectManager && (
                        <span className="text-gray-500 truncate max-w-[100px]">PM: {group.projectManager}</span>
                      )}
                    </div>

                    {/* Job Count Badge */}
                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium whitespace-nowrap">
                      {group.jobs.length} job{group.jobs.length !== 1 ? "s" : ""}
                    </span>

                    {/* Active indicator */}
                    {isActive && (
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          activeState === "running"
                            ? "bg-green-500 animate-pulse"
                            : "bg-yellow-500"
                        }`}
                      />
                    )}

                    {/* Remove button - show on hover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeQuoteJobs(group.quoteNumber);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-200 transition-opacity flex-shrink-0"
                      title="Remove all jobs for this quote"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Hover Tooltip - Jobs List */}
                  {isHovered && group.jobs.length > 0 && (
                    <div
                      className="absolute z-50 top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                      onMouseEnter={() => setHoveredQuote(group.quoteNumber)}
                      onMouseLeave={() => setHoveredQuote(null)}
                    >
                      <div className="px-3 py-2 bg-slate-200 border-b border-gray-200">
                        <div className="text-xs text-gray-500">Jobs in this quote:</div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {group.jobs.map((job) => {
                          const jobState = activeSessions[job.id]?.state;
                          return (
                            <div
                              key={job.id}
                              className="px-3 py-2 border-b border-gray-100 last:border-0 hover:bg-slate-200"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-mono text-sm text-gray-900">{job.jobNumber}</span>
                                {jobState && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    jobState === "running"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}>
                                    {jobState}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 truncate">
                                {job.name || job.productCode || "—"}
                              </div>
                              {job.qty && (
                                <div className="text-xs text-gray-500">Qty: {job.qty}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Hook to manage pinned jobs
 * Use this in the parent component to add jobs when PROOF-PDF is completed
 */
export function usePinnedJobs() {
  const addPinnedJob = useCallback((job, completedOperation) => {
    console.log("📌 addPinnedJob called:", { job, completedOperation });
    if (completedOperation !== "PROOF-PDF") return;

    try {
      const stored = localStorage.getItem(PINNED_JOBS_STORAGE_KEY);
      const existing = stored ? JSON.parse(stored) : [];

      // Check if already pinned
      if (existing.some((j) => j.id === job.id)) {
        console.log("📌 Job already pinned, skipping:", job.id);
        return;
      }

      const pinnedJob = {
        id: job.id,
        jobNumber: job.jobNumber,
        name: job.name,
        productCode: job.productCode,
        qty: job.qty,
        orderNumber: job.orderNumber,
        quoteNumber: job.quoteNumber,
        projectName: job.projectName,
        customer: job.customer,
        projectManager: job.projectManager,
        completedOperation,
        pinnedAt: new Date().toISOString(),
      };

      console.log("📌 Saving pinned job:", pinnedJob);
      const updated = [pinnedJob, ...existing].slice(0, 50); // Keep max 50 pinned jobs
      localStorage.setItem(PINNED_JOBS_STORAGE_KEY, JSON.stringify(updated));

      // Dispatch event to update PinnedJobs component immediately
      window.dispatchEvent(new CustomEvent(PINNED_JOBS_UPDATED_EVENT));
    } catch (err) {
      console.error("Error adding pinned job:", err);
    }
  }, []);

  const removePinnedJob = useCallback((jobId) => {
    try {
      const stored = localStorage.getItem(PINNED_JOBS_STORAGE_KEY);
      if (!stored) return;
      const existing = JSON.parse(stored);
      const updated = existing.filter((j) => j.id !== jobId);
      localStorage.setItem(PINNED_JOBS_STORAGE_KEY, JSON.stringify(updated));

      // Dispatch event to update PinnedJobs component immediately
      window.dispatchEvent(new CustomEvent(PINNED_JOBS_UPDATED_EVENT));
    } catch (err) {
      console.error("Error removing pinned job:", err);
    }
  }, []);

  return { addPinnedJob, removePinnedJob };
}
